import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Loader2, AlertCircle, X, ChevronLeft, ChevronRight, Download } from 'lucide-react'
import { clsx } from 'clsx'

import {
  franchiseInquiryApi,
  type FranchiseInquiryListItem,
  type FranchiseType,
  type InquiryStatus,
} from '@/api/lcMarketing'
import { FRANCHISE_TYPE_LABELS, SIDO_OPTIONS, labelOrRaw } from './inquiryLabels'

const STATUS_OPTIONS: { value: InquiryStatus | ''; label: string }[] = [
  { value: '', label: '전체' },
  { value: 'pending', label: '대기중' },
  { value: 'contacted', label: '연락완료' },
  { value: 'done', label: '종결' },
]

const STATUS_LABEL: Record<InquiryStatus, string> = {
  pending: '대기중',
  contacted: '연락완료',
  done: '종결',
}

const STATUS_BADGE: Record<InquiryStatus, string> = {
  pending: 'bg-amber-100 text-amber-700',
  contacted: 'bg-blue-100 text-blue-700',
  done: 'bg-gray-100 text-gray-700',
}

const TYPE_OPTIONS: { value: FranchiseType | ''; label: string }[] = [
  { value: '', label: '전체 유형' },
  { value: 'academy', label: '학원' },
  { value: 'tutoring', label: '교습소' },
]

const SIDO_FILTER_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: '전체 시도' },
  ...SIDO_OPTIONS.map((s) => ({ value: s, label: s })),
]

const PAGE_SIZE = 20

export default function FranchiseInquiriesTab() {
  const [status, setStatus] = useState<InquiryStatus | ''>('')
  const [franchiseType, setFranchiseType] = useState<FranchiseType | ''>('')
  const [sido, setSido] = useState<string>('')
  const [page, setPage] = useState(0)
  const [selected, setSelected] = useState<FranchiseInquiryListItem | null>(null)
  const queryClient = useQueryClient()

  const { data, isLoading, isError } = useQuery({
    queryKey: ['franchise-inquiries', { status, franchiseType, sido, page }],
    queryFn: () =>
      franchiseInquiryApi.list({
        status: status || undefined,
        franchiseType: franchiseType || undefined,
        sido: sido || undefined,
        page,
        size: PAGE_SIZE,
      }),
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, next }: { id: number; next: InquiryStatus }) =>
      franchiseInquiryApi.updateStatus(id, next),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['franchise-inquiries'] })
    },
  })

  const handleDownload = async () => {
    try {
      const { blob, filename } = await franchiseInquiryApi.export({
        status: status || undefined,
        franchiseType: franchiseType || undefined,
        sido: sido || undefined,
      })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
    } catch {
      alert('CSV 다운로드에 실패했습니다.')
    }
  }

  if (isError) {
    return (
      <div className="p-6 text-red-600 flex items-center gap-2">
        <AlertCircle className="w-5 h-5" /> 목록을 불러오지 못했습니다.
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-2 flex-wrap">
        <select
          value={status}
          onChange={(e) => {
            setStatus(e.target.value as InquiryStatus | '')
            setPage(0)
          }}
          className="border border-gray-300 rounded px-3 py-1.5 text-sm"
        >
          {STATUS_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <select
          value={franchiseType}
          onChange={(e) => {
            setFranchiseType(e.target.value as FranchiseType | '')
            setPage(0)
          }}
          className="border border-gray-300 rounded px-3 py-1.5 text-sm"
        >
          {TYPE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <select
          value={sido}
          onChange={(e) => {
            setSido(e.target.value)
            setPage(0)
          }}
          className="border border-gray-300 rounded px-3 py-1.5 text-sm"
        >
          {SIDO_FILTER_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <span className="text-sm text-gray-500">
          {data ? `${data.totalElements}건` : ''}
        </span>
        <button
          type="button"
          onClick={handleDownload}
          className="ml-auto inline-flex items-center gap-1 px-3 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-50"
        >
          <Download className="w-4 h-4" />
          CSV 다운로드
        </button>
      </div>

      <div className="overflow-x-auto border border-gray-200 rounded">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-gray-600">
            <tr>
              <Th>신청일</Th>
              <Th>이름</Th>
              <Th>전화</Th>
              <Th>가맹 유형</Th>
              <Th>지역</Th>
              <Th>마케팅</Th>
              <Th>상태</Th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr>
                <td colSpan={7} className="py-12 text-center text-gray-400">
                  <Loader2 className="w-5 h-5 animate-spin inline" />
                </td>
              </tr>
            )}
            {data && data.content.length === 0 && !isLoading && (
              <tr>
                <td colSpan={7} className="py-12 text-center text-gray-400">
                  접수된 문의가 없습니다.
                </td>
              </tr>
            )}
            {data?.content.map((row) => {
              const types = row.franchiseTypes.map((t) =>
                labelOrRaw(FRANCHISE_TYPE_LABELS, t),
              )
              const region =
                row.sido && row.sigungu ? `${row.sido} ${row.sigungu}` : '—'
              return (
                <tr
                  key={row.id}
                  className="border-t border-gray-100 hover:bg-gray-50 cursor-pointer"
                  onClick={() => setSelected(row)}
                >
                  <Td>{formatDate(row.createdAt)}</Td>
                  <Td>{row.name}</Td>
                  <Td>{row.phone}</Td>
                  <Td>
                    {types.map((p, i) => (
                      <span key={i} className={p.missing ? 'text-gray-400' : ''}>
                        {p.text}
                        {i < types.length - 1 ? ', ' : ''}
                      </span>
                    ))}
                  </Td>
                  <Td>
                    <span className={region === '—' ? 'text-gray-400' : ''}>{region}</span>
                  </Td>
                  <Td>{row.marketingConsent ? 'Y' : 'N'}</Td>
                  <Td>
                    <span
                      className={clsx('px-2 py-0.5 rounded text-xs', STATUS_BADGE[row.status])}
                    >
                      {STATUS_LABEL[row.status]}
                    </span>
                  </Td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {data && data.totalPages > 1 && (
        <Pager page={data.page} totalPages={data.totalPages} onChange={setPage} />
      )}

      {selected && (
        <DetailPanel
          item={selected}
          onClose={() => setSelected(null)}
          onChangeStatus={(next) =>
            updateMutation.mutate(
              { id: selected.id, next },
              { onSuccess: () => setSelected({ ...selected, status: next }) },
            )
          }
          isUpdating={updateMutation.isPending}
        />
      )}
    </div>
  )
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th className="text-left px-3 py-2 font-medium border-b border-gray-200 whitespace-nowrap">
      {children}
    </th>
  )
}

function Td({ children }: { children: React.ReactNode }) {
  return <td className="px-3 py-2 whitespace-nowrap">{children}</td>
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString('ko-KR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function Pager({
  page,
  totalPages,
  onChange,
}: {
  page: number
  totalPages: number
  onChange: (next: number) => void
}) {
  return (
    <div className="flex items-center justify-end gap-2 text-sm">
      <button
        type="button"
        disabled={page === 0}
        onClick={() => onChange(page - 1)}
        className="p-1 disabled:opacity-30"
      >
        <ChevronLeft className="w-4 h-4" />
      </button>
      <span>
        {page + 1} / {totalPages}
      </span>
      <button
        type="button"
        disabled={page >= totalPages - 1}
        onClick={() => onChange(page + 1)}
        className="p-1 disabled:opacity-30"
      >
        <ChevronRight className="w-4 h-4" />
      </button>
    </div>
  )
}

interface DetailPanelProps {
  item: FranchiseInquiryListItem
  onClose: () => void
  onChangeStatus: (next: InquiryStatus) => void
  isUpdating: boolean
}

function DetailPanel({ item, onClose, onChangeStatus, isUpdating }: DetailPanelProps) {
  const types = item.franchiseTypes.map((t) => labelOrRaw(FRANCHISE_TYPE_LABELS, t))
  const region = item.sido && item.sigungu ? `${item.sido} ${item.sigungu}` : '—'
  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex justify-end"
      onClick={onClose}
      role="dialog"
      aria-modal
    >
      <div
        className="w-full max-w-md bg-white h-full overflow-y-auto p-6 flex flex-col gap-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between">
          <h3 className="text-lg font-semibold">가맹 문의 상세</h3>
          <button onClick={onClose} aria-label="닫기">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <DL label="신청일" value={formatDate(item.createdAt)} />
        <DL label="이름" value={item.name} />
        <DL label="전화" value={item.phone} />
        <div className="flex flex-col gap-1">
          <span className="text-xs text-gray-500">가맹 유형</span>
          <span className="text-sm">
            {types.map((p, i) => (
              <span key={i} className={p.missing ? 'text-gray-400' : 'text-gray-900'}>
                {p.text}
                {i < types.length - 1 ? ', ' : ''}
              </span>
            ))}
          </span>
        </div>
        <DL label="지역" value={region} missing={region === '—'} />
        <div className="flex flex-col gap-1">
          <span className="text-xs text-gray-500">문의 사항</span>
          <p className="text-sm text-gray-900 whitespace-pre-wrap break-words">
            {item.inquiryText}
          </p>
        </div>
        <DL
          label="개인정보 수집·이용 동의"
          value={
            item.privacyConsentedAt
              ? `동의 (${formatDate(item.privacyConsentedAt)})`
              : '미동의'
          }
        />
        <DL
          label="마케팅 정보 수신 동의"
          value={
            item.marketingConsent && item.marketingConsentedAt
              ? `동의 (${formatDate(item.marketingConsentedAt)})`
              : '미동의'
          }
        />

        <div className="flex flex-col gap-2 pt-3 border-t border-gray-200">
          <label className="text-sm font-medium">상태</label>
          <select
            value={item.status}
            onChange={(e) => onChangeStatus(e.target.value as InquiryStatus)}
            disabled={isUpdating}
            className="border border-gray-300 rounded px-3 py-2 text-sm"
          >
            <option value="pending">대기중</option>
            <option value="contacted">연락완료</option>
            <option value="done">종결</option>
          </select>
        </div>
      </div>
    </div>
  )
}

function DL({
  label,
  value,
  missing,
}: {
  label: string
  value: string
  missing?: boolean
}) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs text-gray-500">{label}</span>
      <span
        className={clsx(
          'text-sm break-words',
          missing ? 'text-gray-400' : 'text-gray-900',
        )}
      >
        {value}
      </span>
    </div>
  )
}
